<template>
	<div class="survey">
		<div class="survey-container">
			<div class="d-flex justify-space-between mb-7">
				<div>
					<h2 class="mr-5">Enkät om resvanor</h2>
					<p>– tack för att du bidrar!</p>
				</div>
				<div style="text-align: right; max-width: 200px; max-height: 200px;" v-if="endUser && endUser.client && endUser.client.logo">
          <img
            style="max-width: 200px; max-height: 200px;"
            :src="$apiUrl + '/storage/' + endUser.client.logo"
            :alt="$processText('{clientName} Logo')"
          />
				</div>
			</div>

			<div v-if="stepIndex === 0">
				<p>
					I denna enkät får du svara på hur ditt vardagsresande ser ut idag och under samma period tidigare år. Dina svar är värdefulla – oavsett om du färdas långt, kort, ofta eller sällan. Med hjälp av dina svar kan vi skapa ännu bättre förutsättningar för smart resande åt dig och oss alla.
				</p>
				<p>Undersökningen genomförs av Nudgd på uppdrag av Uppsala kommun. Läs mer om hur <a :href="$processText('https://data.nudgd.se/{slug}')" target="_blank">dina personuppgifter hanteras.</a></p>
				<p>Det går snabbt att svara.</p>

				<h2 class="mt-10 mb-2">Tänk på ditt resande <span style="color: #0B24FB;">till och från jobbet, de senaste veckorna</span>.</h2>
				<p class="mb-0"><em class="smaller-em">Fundera på vad som känns rimligt och fyll i det du tror.</em></p>


				<h4 class="mt-3 mb-3" :class="{surveyError: errors.includes('mainModeOfTransport')}">Vad anser du att ditt huvudsakliga färdsätt var för dina resor <span style="color: #0B24FB;">de senaste veckorna</span>?</h4>

				<ModeOfTransport :key="1" :value="mainModeOfTransport" @selected="selectModeOfTransport" />

				<div class="nudgd-btn inline survey-submit mt-5" @click="confirmFirstStep()" v-if=" ! firstStepConfirmed">Gå vidare</div>
				<div style="clear:both;"></div>

				<template v-if="firstStepConfirmed">

					<h4 class="mt-2 mb-3">Hur ofta använde du följande färdsätt, i genomsnitt, <span style="color: #0B24FB;">till och från jobbet, de senaste veckorna</span>?</h4>
					<p class="mb-2"><em class="smaller-em">(Använder du olika färdmedel för samma resa så räkna med alla. Runda av till närmaste heltal, t.ex. om du bara använder bil tre gånger per månad blir det 3/4 = 0,75 –> 1. Vänligen svara på alla.)</em></p>
					<div class="survey-frequency-chips walk">
						<h3 :class="{surveyError: errors.includes('frequencyWalk')}">🚶 Till fots</h3>
						<div class="survey-frequency-chips-container">
							<div class="survey-frequency-chips-container-chip walking" :class="{active: frequencyWalk == number}" v-for="number in [0, 1, 2, 3, 4, 5, 6, 7]" :key="number" @click="selectFrequency('frequencyWalk', number)">{{ number }}</div>
						</div>
						<div class="survey-frequency-chips-helpers right">
							<div>dagar per vecka</div>
						</div>
					</div>
					<div class="survey-frequency-chips">
						<h3 :class="{surveyError: errors.includes('frequencyBike')}">🚲 Cykel</h3>
						<div class="survey-frequency-chips-container">
							<div class="survey-frequency-chips-container-chip bike" :class="{active: frequencyBike == number}" v-for="number in [0, 1, 2, 3, 4, 5, 6, 7]" :key="number" @click="selectFrequency('frequencyBike', number)">{{ number }}</div>
						</div>
						<div class="survey-frequency-chips-helpers right">
							<div>dagar per vecka</div>
						</div>
					</div>
					<div class="survey-frequency-chips">
						<h3 :class="{surveyError: errors.includes('frequencyTransit')}">🚌 Kollektivt</h3>
						<div class="survey-frequency-chips-container">
							<div class="survey-frequency-chips-container-chip transit" :class="{active: frequencyTransit == number}" v-for="number in [0, 1, 2, 3, 4, 5, 6, 7]" :key="number" @click="selectFrequency('frequencyTransit', number)">{{ number }}</div>
						</div>
						<div class="survey-frequency-chips-helpers right">
							<div>dagar per vecka</div>
						</div>
					</div>
					<div class="survey-frequency-chips">
						<h3 :class="{surveyError: errors.includes('frequencyCar')}">🚗 Bil - själv eller med hushållsmedlem</h3>
						<div class="survey-frequency-chips-container">
							<div class="survey-frequency-chips-container-chip car" :class="{active: frequencyCar == number}" v-for="number in [0, 1, 2, 3, 4, 5, 6, 7]" :key="number" @click="selectFrequency('frequencyCar', number)">{{ number }}</div>
						</div>
						<div class="survey-frequency-chips-helpers right">
							<div>dagar per vecka</div>
						</div>
					</div>
					<div class="survey-frequency-chips">
						<h3 :class="{surveyError: errors.includes('frequencyCarCoride')}">🚙 Bil - samåkning med annan</h3>
						<div class="survey-frequency-chips-container">
							<div class="survey-frequency-chips-container-chip car" :class="{active: frequencyCarCoride == number}" v-for="number in [0, 1, 2, 3, 4, 5, 6, 7]" :key="number" @click="selectFrequency('frequencyCarCoride', number)">{{ number }}</div>
						</div>
						<div class="survey-frequency-chips-helpers right">
							<div>dagar per vecka</div>
						</div>
					</div>
					<div class="survey-frequency-chips">
						<h3 :class="{surveyError: errors.includes('frequencyMisc')}">Övrigt</h3>
						<div class="survey-frequency-chips-container">
							<div class="survey-frequency-chips-container-chip" :class="{active: frequencyMisc == number}" v-for="number in [0, 1, 2, 3, 4, 5, 6, 7]" :key="number" @click="selectFrequency('frequencyMisc', number)">{{ number }}</div>
						</div>
						<div class="survey-frequency-chips-helpers right">
							<div>dagar per vecka</div>
						</div>
					</div>

					<template v-if="frequencyCar || frequencyMisc || frequencyWalk || frequencyTransit || frequencyCarCoride || frequencyBike">
						<h4 class="mt-2 mb-1">I genomsnitt <span style="color: #0B24FB;"> de senaste veckorna</span>, hur många gånger har du färdats någonstans?</h4>
						<p class="mb-2"><em class="smaller-em">(Tur och retur räknas som två enkelresor.)</em></p>

						<div class="survey-thirds mt-3">
							<div class="survey-thirds-first" :class="{surveyError: errors.includes('numberOfTrips')}">Antal enkelresor per vecka:</div>

							<div class="survey-thirds-middle">
								<vue-slider dotSize="20" :max="35" v-model="numberOfTrips"></vue-slider>
							</div>

							<div class="survey-thirds-last"><v-text-field placeholder="0" v-model="numberOfTrips"></v-text-field> resor</div>
						</div>



						<h4 class="mt-6 mb-6">Hur långt har du i genomsnitt färdats per vecka med följande färdsätt, <span style="color: #0B24FB;">till och från jobbet, de senaste veckorna</span>?</h4>
						<p class="mb-2"><em class="smaller-em">(Räkna med både vägen till jobbet och tillbaka. Du kan antingen dra i ikonen eller skriva in avståndet direkt i kolumnen till höger.)</em></p>
						<div class="survey-thirds walking mt-3" v-if="frequencyWalk > 0">
							<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('weeklyWalking')}">Till fots</div>
							<div class="survey-thirds-middle"><vue-slider :max="200" v-model="weeklyWalking"><template v-slot:dot><div class="custom-dot alt">🚶</div></template></vue-slider></div>
							<div class="survey-thirds-last"><v-text-field placeholder="0" v-model="weeklyWalking"></v-text-field> km</div>
						</div>

						<div class="survey-thirds bike mt-3" v-if="frequencyBike > 0">
							<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('weeklyBike')}">Cykel</div>
							<div class="survey-thirds-middle"><vue-slider :max="200" v-model="weeklyBike"><template v-slot:dot><div class="custom-dot alt">🚲</div></template></vue-slider></div>
							<div class="survey-thirds-last"><v-text-field placeholder="0" v-model="weeklyBike"></v-text-field> km</div>
						</div>

						<div class="survey-thirds transit mt-3" v-if="frequencyTransit > 0">
							<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('weeklyTransit')}">Kollektivt</div>
							<div class="survey-thirds-middle"><vue-slider :max="1000" v-model="weeklyTransit"><template v-slot:dot><div class="custom-dot alt">🚌</div></template></vue-slider></div>
							<div class="survey-thirds-last"><v-text-field placeholder="0" v-model="weeklyTransit"></v-text-field> km</div>
						</div>

						<div class="survey-thirds car mt-3" v-if="frequencyCar > 0">
							<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('weeklyCar')}">Bil - själv eller med hushållsmedlem</div>
							<div class="survey-thirds-middle"><vue-slider :max="1000" v-model="weeklyCar"><template v-slot:dot><div class="custom-dot alt">🚗</div></template></vue-slider></div>
							<div class="survey-thirds-last"><v-text-field placeholder="0" v-model="weeklyCar"></v-text-field> km</div>
						</div>

						<div class="survey-thirds car carCoride mt-3" v-if="frequencyCarCoride > 0">
							<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('weeklyCarCoride')}">Bil - samåkning med annan</div>
							<div class="survey-thirds-middle"><vue-slider :max="1000" v-model="weeklyCarCoride"><template v-slot:dot><div class="custom-dot alt">🚙</div></template></vue-slider></div>
							<div class="survey-thirds-last"><v-text-field placeholder="0" v-model="weeklyCarCoride"></v-text-field> km</div>
						</div>

						<div class="survey-thirds misc mt-3" v-if="frequencyMisc > 0">
							<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('weeklyMisc')}">
								<v-tooltip bottom>
							      <template v-slot:activator="{ on, attrs }"><span v-bind="attrs" v-on="on">Övrigt <div class="tooltip-container"><span class="icon-info"></span></div></span></template>
							      <span>t.ex. elsparkcykel, skateboard etc."</span>
							    </v-tooltip>
							</div>
							<div class="survey-thirds-middle"><vue-slider dotSize="20" :max="200" v-model="weeklyMisc"></vue-slider></div>
							<div class="survey-thirds-last"><v-text-field placeholder="0" v-model="weeklyMisc"></v-text-field> km</div>
						</div>

						<div class="survey-thirds-sum"><strong>SUMMA</strong> <span>{{ weeklySum }} km</span></div>

						<div class="survey-sum mt-6">
							Om du färdas enligt ovan alla årets veckor blir det <strong>{{ yearlyDistance }} km</strong>.
							<br/>
							<br/>
							<em>Om reslängden per vecka verkar rimlig, vänligen gå vidare i enkäten. Om inte, justera i frågorna ovan.</em></div>


						<div v-if="weeklyCar">
							<h4 class="mt-5">Hur är dina bilresor fördelade utifrån drivmedel?</h4>
							<p class="mb-2"><em class="smaller-em">(Summan för de olika drivmedlen måste matcha med Bil - själv eller med hushållsmedlem ovan <strong>({{ weeklyCar }} km)</strong>. Du kan antingen dra i ikonen eller skriva in avståndet direkt i kolumnen till höger.)</em></p>

							<div class="survey-thirds electricity mt-3">
								<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('weeklyElectricity')}">El</div>
								<div class="survey-thirds-middle"><vue-slider dotSize="20" :max="1000" v-model="weeklyElectricity"></vue-slider></div>
								<div class="survey-thirds-last"><v-text-field placeholder="0" v-model="weeklyElectricity"></v-text-field> km</div>
							</div>

							<div class="survey-thirds hybrid mt-3">
								<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('weeklyHybrid')}">Hybrid/gas</div>
								<div class="survey-thirds-middle"><vue-slider dotSize="20" :max="1000" v-model="weeklyHybrid"></vue-slider></div>
								<div class="survey-thirds-last"><v-text-field placeholder="0" v-model="weeklyHybrid"></v-text-field> km</div>
							</div>

							<div class="survey-thirds gas mt-3">
								<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('weeklyGas')}">Bensin/diesel<br/>/etanol</div>
								<div class="survey-thirds-middle"><vue-slider dotSize="20" :max="1000" v-model="weeklyGas"></vue-slider></div>
								<div class="survey-thirds-last"><v-text-field placeholder="0" v-model="weeklyGas"></v-text-field> km</div>
							</div>
						</div>
					</template>

					<div class="nudgd-btn inline survey-submit mt-10" @click="nextStep()">Gå vidare</div>
					<div style="clear:both;"></div>
				</template>
			</div>

			<div v-if="stepIndex === 1">

				<h2 class="mt-10 mb-2">Tänk på ditt resande <span style="color: #00A19A;">till och från jobbet, under denna tiden förra året</span>.</h2>
				<p class="mb-0"><em class="smaller-em">Fundera på vad som känns rimligt och fyll i det du tror.</em></p>

				<h4 class="mt-3 mb-3" :class="{surveyError: errors.includes('previousMainModeOfTransport')}">Vad anser du att ditt huvudsakliga färdsätt var för dina resor <span style="color: #00A19A;">till och från jobbet, under denna tiden förra året</span>?</h4>
				<ModeOfTransport :value="previousMainModeOfTransport" :key="2" @selected="selectpreviousModeOfTransport" />

				<h4 class="mt-2 mb-3">Hur ofta använde du följande färdsätt, i genomsnitt, <span style="color: #00A19A;">till och från jobbet, under denna tiden förra året</span>?</h4>
				<p class="mb-2"><em class="smaller-em">(Använde du olika färdmedel för samma resa så räkna med alla. Runda av till närmaste heltal, t.ex. om du bara använde bil tre gånger per månad blir det 3/4 = 0,75 –> 1. Vänligen svara på alla.)</em></p>
				<div class="survey-frequency-chips walk">
					<h3 :class="{surveyError: errors.includes('previousFrequencyWalk')}">🚶 Till fots</h3>
					<div class="survey-frequency-chips-container">
						<div class="survey-frequency-chips-container-chip walking" :class="{active: previousFrequencyWalk == number}" v-for="number in [0, 1, 2, 3, 4, 5, 6, 7]" :key="number" @click="selectFrequency('previousFrequencyWalk', number)">{{ number }}</div>
					</div>
					<div class="survey-frequency-chips-helpers right">
						<div>dagar per vecka</div>
					</div>
				</div>
				<div class="survey-frequency-chips">
					<h3 :class="{surveyError: errors.includes('previousFrequencyBike')}">🚲 Cykel</h3>
					<div class="survey-frequency-chips-container">
						<div class="survey-frequency-chips-container-chip bike" :class="{active: previousFrequencyBike == number}" v-for="number in [0, 1, 2, 3, 4, 5, 6, 7]" :key="number" @click="selectFrequency('previousFrequencyBike', number)">{{ number }}</div>
					</div>
					<div class="survey-frequency-chips-helpers right">
						<div>dagar per vecka</div>
					</div>
				</div>
				<div class="survey-frequency-chips">
					<h3 :class="{surveyError: errors.includes('previousFrequencyTransit')}">🚌 Kollektivt</h3>
					<div class="survey-frequency-chips-container">
						<div class="survey-frequency-chips-container-chip transit" :class="{active: previousFrequencyTransit == number}" v-for="number in [0, 1, 2, 3, 4, 5, 6, 7]" :key="number" @click="selectFrequency('previousFrequencyTransit', number)">{{ number }}</div>
					</div>
					<div class="survey-frequency-chips-helpers right">
						<div>dagar per vecka</div>
					</div>
				</div>
				<div class="survey-frequency-chips">
					<h3 :class="{surveyError: errors.includes('previousFrequencyCar')}">🚗 Bil - själv eller med hushållsmedlem</h3>
					<div class="survey-frequency-chips-container">
						<div class="survey-frequency-chips-container-chip car" :class="{active: previousFrequencyCar == number}" v-for="number in [0, 1, 2, 3, 4, 5, 6, 7]" :key="number" @click="selectFrequency('previousFrequencyCar', number)">{{ number }}</div>
					</div>
					<div class="survey-frequency-chips-helpers right">
						<div>dagar per vecka</div>
					</div>
				</div>
				<div class="survey-frequency-chips">
					<h3 :class="{surveyError: errors.includes('previousFrequencyCarCoride')}">🚙 Bil - samåkning med annan</h3>
					<div class="survey-frequency-chips-container">
						<div class="survey-frequency-chips-container-chip car" :class="{active: previousFrequencyCarCoride == number}" v-for="number in [0, 1, 2, 3, 4, 5, 6, 7]" :key="number" @click="selectFrequency('previousFrequencyCarCoride', number)">{{ number }}</div>
					</div>
					<div class="survey-frequency-chips-helpers right">
						<div>dagar per vecka</div>
					</div>
				</div>
				<div class="survey-frequency-chips">
					<h3 :class="{surveyError: errors.includes('previousFrequencyMisc')}">Övrigt</h3>
					<div class="survey-frequency-chips-container">
						<div class="survey-frequency-chips-container-chip" :class="{active: previousFrequencyMisc == number}" v-for="number in [0, 1, 2, 3, 4, 5, 6, 7]" :key="number" @click="selectFrequency('previousFrequencyMisc', number)">{{ number }}</div>
					</div>
					<div class="survey-frequency-chips-helpers right">
						<div>dagar per vecka</div>
					</div>
				</div>

				<template v-if="previousFrequencyCar || previousFrequencyMisc || previousFrequencyWalk || previousFrequencyTransit || previousFrequencyCarCoride || previousFrequencyBike">
					<h4 class="mt-2 mb-1">I genomsnitt <span style="color: #00A19A;">under denna tiden förra året</span>, hur många gånger har du färdats till och från jobbet?</h4>
					<p class="mb-2"><em class="smaller-em">(Tur och retur räknas som två enkelresor.)</em></p>

					<div class="survey-thirds mt-3">
						<div class="survey-thirds-first" :class="{surveyError: errors.includes('previousNumberOfTrips')}">Antal enkelresor per vecka:</div>

						<div class="survey-thirds-middle">
							<vue-slider dotSize="20" :max="35" v-model="previousNumberOfTrips"></vue-slider>
						</div>

						<div class="survey-thirds-last"><v-text-field hide-details v-model="previousNumberOfTrips"></v-text-field> resor</div>
					</div>


					<h4 class="mt-6 mb-6">Hur långt färdades du i genomsnitt per vecka med följande färdsätt, <span style="color: #00A19A;">till och från jobbet, under denna tiden förra året</span>?</h4>
					<p class="mb-2"><em class="smaller-em">(Räkna med både vägen till jobbet och tillbaka. Du kan antingen dra i ikonen eller skriva in avståndet direkt i kolumnen till höger.)</em></p>
					<div class="survey-thirds walking mt-3" v-if="previousFrequencyWalk > 0">
						<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('previousWeeklyWalking')}">Till fots</div>
						<div class="survey-thirds-middle"><vue-slider :max="200" v-model="previousWeeklyWalking"><template v-slot:dot><div class="custom-dot alt">🚶</div></template></vue-slider></div>
						<div class="survey-thirds-last"><v-text-field hide-details v-model="previousWeeklyWalking"></v-text-field> km</div>
					</div>

					<div class="survey-thirds bike mt-3" v-if="previousFrequencyBike > 0">
						<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('previousWeeklyBike')}">Cykel</div>
						<div class="survey-thirds-middle"><vue-slider :max="200" v-model="previousWeeklyBike"><template v-slot:dot><div class="custom-dot alt">🚲</div></template></vue-slider></div>
						<div class="survey-thirds-last"><v-text-field hide-details v-model="previousWeeklyBike"></v-text-field> km</div>
					</div>

					<div class="survey-thirds transit mt-3" v-if="previousFrequencyTransit > 0">
						<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('previousWeeklyTransit')}">Kollektivt</div>
						<div class="survey-thirds-middle"><vue-slider :max="1000" v-model="previousWeeklyTransit"><template v-slot:dot><div class="custom-dot alt">🚌</div></template></vue-slider></div>
						<div class="survey-thirds-last"><v-text-field hide-details v-model="previousWeeklyTransit"></v-text-field> km</div>
					</div>

					<div class="survey-thirds car mt-3" v-if="previousFrequencyCar > 0">
						<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('previousWeeklyCar')}">Bil - själv eller med hushållsmedlem</div>
						<div class="survey-thirds-middle"><vue-slider :max="1000" v-model="previousWeeklyCar"><template v-slot:dot><div class="custom-dot alt">🚗</div></template></vue-slider></div>
						<div class="survey-thirds-last"><v-text-field hide-details v-model="previousWeeklyCar"></v-text-field> km</div>
					</div>

					<div class="survey-thirds car carCoride mt-3" v-if="previousFrequencyCarCoride > 0">
						<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('previousWeeklyCarCoride')}">Bil - samåkning med annan</div>
						<div class="survey-thirds-middle"><vue-slider :max="1000" v-model="previousWeeklyCarCoride"><template v-slot:dot><div class="custom-dot alt">🚙</div></template></vue-slider></div>
						<div class="survey-thirds-last"><v-text-field hide-details v-model="previousWeeklyCarCoride"></v-text-field> km</div>
					</div>

					<div class="survey-thirds misc mt-3" v-if="previousFrequencyMisc > 0">
						<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('previousWeeklyMisc')}">
							<v-tooltip bottom>
						      <template v-slot:activator="{ on, attrs }"><span v-bind="attrs" v-on="on">Övrigt <div class="tooltip-container"><span class="icon-info"></span></div></span></template>
						      <span>t.ex. elsparkcykel, skateboard etc."</span>
						    </v-tooltip>
						</div>
						<div class="survey-thirds-middle"><vue-slider dotSize="20" :max="200" v-model="previousWeeklyMisc"></vue-slider></div>
						<div class="survey-thirds-last"><v-text-field hide-details v-model="previousWeeklyMisc"></v-text-field> km</div>
					</div>

					<div class="survey-thirds-sum"><strong>SUMMA</strong> <span>{{ previousWeeklySum }} km</span></div>

					<div class="survey-sum mt-6">
						Om du färdades enligt ovan alla årets veckor blev det <strong>{{ previousYearlyDistance }} km</strong>.
						<br/>
						<br/>
						<em>Om reslängden per vecka verkar rimlig, vänligen gå vidare i enkäten. Om inte, justera i frågorna ovan.</em></div>


					<div v-if="previousWeeklyCar">
						<h4 class="mt-5">Hur var dina bilresor fördelade utifrån drivmedel?</h4>
						<p class="mb-2"><em class="smaller-em">(Summan för de olika drivmedlen måste matcha med Bil - själv eller med hushållsmedlem ovan <strong>({{ previousWeeklyCar }} km)</strong>. Du kan antingen dra i ikonen eller skriva in avståndet direkt i kolumnen till höger.)</em></p>

						<div class="survey-thirds electricity mt-3">
							<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('previousWeeklyElectricity')}">El</div>
							<div class="survey-thirds-middle"><vue-slider dotSize="20" :max="1000" v-model="previousWeeklyElectricity"></vue-slider></div>
							<div class="survey-thirds-last"><v-text-field placeholder="0" v-model="previousWeeklyElectricity"></v-text-field> km</div>
						</div>

						<div class="survey-thirds hybrid mt-3">
							<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('previousWeeklyHybrid')}">Hybrid/gas</div>
							<div class="survey-thirds-middle"><vue-slider dotSize="20" :max="1000" v-model="previousWeeklyHybrid"></vue-slider></div>
							<div class="survey-thirds-last"><v-text-field placeholder="0" v-model="previousWeeklyHybrid"></v-text-field> km</div>
						</div>

						<div class="survey-thirds gas mt-3">
							<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('previousWeeklyGas')}">Bensin/diesel<br/>/etanol</div>
							<div class="survey-thirds-middle"><vue-slider dotSize="20" :max="1000" v-model="previousWeeklyGas"></vue-slider></div>
							<div class="survey-thirds-last"><v-text-field placeholder="0" v-model="previousWeeklyGas"></v-text-field> km</div>
						</div>
					</div>
				</template>

				<div class="nudgd-btn inline survey-submit mt-10" @click="nextStep()">Gå vidare</div>
				<div class="nudgd-btn small survey-submit mt-10" @click="prevStep()">Tillbaka</div>
				<div style="clear:both;"></div>
			</div>

			<div v-if="stepIndex === 2">
        <img
          v-if="!serviceImage"
          style="float: right;"
          width="300"
          :src="$apiUrl + '/storage/google-pixel-2-xl-resvana-20-1280x720-umea-1625230507wJMF2.png'"
          alt="Tjänsten Resvana"
        />
        <img
          v-if="serviceImage"
          style="float: right;"
          width="300"
          :src="$apiUrl + '/storage/' + serviceImage"
          alt="Tjänsten Resvana"
        />
				<div class="mt-2 mb-4" :class="{surveyError: errors.includes('usedResvana')}"><strong>Du fick för ett tag sedan ett mail med en länk till en tjänst kallad Resvana.</strong> Den innehåller bland annat info om olika hållbara transportalternativ nära dig och tips om att starta nya resvanor. <strong>Besökte du tjänsten och läste något eller använde kartan etc.?</strong></div>

				<div class="survey-radio-row mb-6">
					<v-radio-group class="mt-0" v-model="usedResvana" row>
						<v-radio label="Ja" value="Ja"></v-radio>
						<v-radio label="Nej" value="Nej"></v-radio>
					</v-radio-group>
				</div>

				<div v-if="usedResvana === 'Ja'">
					<h2>Tjänsten Resvana</h2>
					<h4 class="mt-4" :class="{surveyError: errors.includes('resvanaEffect')}">I vilken utsträckning skulle du säga att Resvana har bidragit till ditt val av färdmedel?</h4>
					<vue-slider marks class="mt-3" :max="10" v-model="resvanaEffect"></vue-slider>
					<div class="mt-10" style="display:flex; font-size: 14px; justify-content: space-between; text-align: center;">
						<div style="position: relative; left:-15px;">Inte<br>alls</div>
						<div style="margin-right: auto;">Väldigt<br>lite</div>
						<div style="position: relative; left:20px;">Helt och<br>hållet</div>
					</div>


					<h4 class="mt-10 mb-4">Tjänsten har fått mig att börja...</h4>

					<div class="survey-radio-row">
						<div class="survey-radio-row-first" :class="{surveyError: errors.includes('moreWalking')}">...promenerera (eller åka rullstol) mer</div>
						<v-radio-group class="mt-0" v-model="more.walking" row>
									<v-radio label="Ja" value="Ja"></v-radio>
									<v-radio label="Nja, men har fått mig att planera att göra det" value="Nja, men har fått mig att planera att göra det"></v-radio>
									<v-radio label="Nej" value="Nej"></v-radio>
							</v-radio-group>
						</div>

						<div class="survey-radio-row">
							<div class="survey-radio-row-first" :class="{surveyError: errors.includes('moreBiking')}">...cykla mer</div>
						<v-radio-group v-model="more.biking" row>
									<v-radio label="Ja" value="Ja"></v-radio>
									<v-radio label="Nja, men har fått mig att planera att göra det" value="Nja, men har fått mig att planera att göra det"></v-radio>
									<v-radio label="Nej" value="Nej"></v-radio>
							</v-radio-group>
						</div>

						<div class="survey-radio-row">
							<div class="survey-radio-row-first" :class="{surveyError: errors.includes('moreTransit')}">...åka mer kollektivtrafik</div>
						<v-radio-group v-model="more.transit" row>
									<v-radio label="Ja" value="Ja"></v-radio>
									<v-radio label="Nja, men har fått mig att planera att göra det" value="Nja, men har fått mig att planera att göra det"></v-radio>
									<v-radio label="Nej" value="Nej"></v-radio>
							</v-radio-group>
						</div>

						<div class="survey-radio-row">
						<div class="survey-radio-row-first" :class="{surveyError: errors.includes('moreCar')}">...åka bil på ett mer hållbart sätt, t.ex. elbil, samåkning, bilpool, etc.</div>
						<v-radio-group v-model="more.car" row>
									<v-radio label="Ja" value="Ja"></v-radio>
									<v-radio label="Nja, men har fått mig att planera att göra det" value="Nja, men har fått mig att planera att göra det"></v-radio>
									<v-radio label="Nej" value="Nej"></v-radio>
							</v-radio-group>
						</div>

                    <h4 class="mb-3 mt-6" :class="{surveyError: errors.includes('employerNudging')}">Vad anser du om att arbetsgivaren gör denna typ av insatser för att underlätta för medarbetare att resa mer hållbart?</h4>
					<vue-slider marks class="pb-10" :max="10" v-model="employerNudging"></vue-slider>
					<div style="display:flex; font-size: 14px; justify-content: space-between; text-align: center;">
						<div style="position: relative; left:-18px;">Väldigt<br>negativt</div>
						<div style="position: relative; left:18px;">Väldigt<br>positivt</div>
					</div>

					<h4 class="mb-3 mt-6" :class="{surveyError: errors.includes('resvanaRecommend')}">Hur troligt är det att du skulle rekommendera Resvana till en kollega eller vän?</h4>
					<vue-slider marks class="pb-10" :max="10" v-model="resvanaRecommend"></vue-slider>
					<div style="display:flex; font-size: 14px; justify-content: space-between; text-align: center;">
						<div style="position: relative; left:-18px;">Inte alls<br>sannolikt</div>
						<div style="position: relative; left:18px;">Väldigt<br>sannolikt</div>
					</div>
				</div>

				<h4 class="mb-3 mt-10">Kommentar <span style="font-weight:normal;">(ej obligatorisk)</span></h4>
				<v-textarea rows="4" hide-details solo v-model="commentary"></v-textarea>
				<div class="survey-help mt-2">(Vänligen lämna inga personuppgifter i detta fält)</div>

				<div class="mt-2 mb-3" :class="{surveyError: errors.includes('usedResvana')}">Vi vill arbeta ihop med dig i vårt arbete med att göra samhällets resande hållbart. Får vi därför framöver be dig om råd och rekommendationer och fråga mer om dina resvanor? Stort tack i så fall! Ange i så fall din epost nedan.</div>
			  <p style="font-size:13px;">Läs mer om <a style="font-weight: 600; text-decoration: none;" :href="$processText('https://data.nudgd.se/{slug}')" target="_blank">hur dina personuppgifter hanteras</a>.</p>

			  <v-text-field dense hide-details placeholder="E-post" v-model="contactMe" outlined></v-text-field>

				<div class="nudgd-btn inline survey-submit mt-10" v-if=" ! thankYou" @click="submitResponse()">Skicka in</div>
				<div class="nudgd-btn small survey-submit mt-10" v-if=" ! thankYou" @click="prevStep()">Tillbaka</div>
				<div style="clear:both;"></div>
			</div>

			<v-progress-linear color="#00A19A" height="10" rounded class="mt-8" :value="(stepIndex + 1) / steps * 100"></v-progress-linear>
		</div>

		    <v-dialog
		    	v-model="thankYou"
		    	width="'100%'"
		    	max-width="900"
		    	scrollable
		    	class="dialog"
		    	persistent
		    >

				<v-card>
					<v-card-title class="card-dialog-title" style="font-weight:700;">
						<div>Tack för att du svarat!</div>
					</v-card-title>

					<v-card-text>
						<p>Du är en viktig del i att skapa ännu bättre förutsättningar för smart vardagsresande åt dig och oss alla.</p>
						<p>Gå gärna till tjänsten Resvana igen för att inspireras till mer hållbart resande.</p>

						<p><a class="nudgd-btn inline survey-submit mt-5" :href="$processText('https://app.resvana.se/{slug}')">Till tjänsten</a></p>
					</v-card-text>
				</v-card>

		    </v-dialog>
	</div>
</template>

<script>
import axios from 'axios'
import VueSlider from 'vue-slider-component'
import 'vue-slider-component/theme/default.css'
import ModeOfTransport from '@/components/ModeOfTransport.vue'

export default {
	components: {VueSlider, ModeOfTransport},

	mounted() {
		this.uuid = Math.random().toString(36).substr(2, 5)
		this.$store.state.surveyId = 10

		// For testing purposes
		if (this.$route.query.page) {
			this.stepIndex = parseInt(this.$route.query.page)
		}

		// Get end user
		axios.get(this.$apiUrl + '/api/end-user/' + this.slug + '?s=1').then(res => {
			this.$store.state.endUser = res.data.endUser
			this.$store.state.client = res.data.endUser.client
			this.$store.state.translations = res.data.translations
		})
	},

	data() {
		return {
			steps: 3,

			mainModeOfTransport: null,
			numberOfTrips: null,

			frequencyWalk: null,
			frequencyBike: null,
			frequencyTransit: null,
			frequencyCar: null,
			frequencyCarCoride: null,
			frequencyMisc: null,

			weeklyWalking: null,
			weeklyBike: null,
			weeklyTransit: null,
			weeklyCar: null,
			weeklyCarCoride: null,
			weeklyMisc: null,

			weeklyElectricity: null,
			weeklyHybrid: null,
			weeklyGas: null,


			previousMainModeOfTransport: null,
			previousNumberOfTrips: null,

			previousFrequencyWalk: null,
			previousFrequencyBike: null,
			previousFrequencyTransit: null,
			previousFrequencyCar: null,
			previousFrequencyCarCoride: null,
			previousFrequencyMisc: null,

			previousWeeklyWalking: null,
			previousWeeklyBike: null,
			previousWeeklyTransit: null,
			previousWeeklyCar: null,
			previousWeeklyCarCoride: null,
			previousWeeklyMisc: null,

			previousWeeklyElectricity: null,
			previousWeeklyHybrid: null,
			previousWeeklyGas: null,


			usedResvana: null,
			resvanaEffect: null,
			more: {
				walking: null,
				biking: null,
				transit: null,
				car: null
			},

			commentary: null,
            employerNudging: null,
			resvanaRecommend: null,
			contactMe: null,

			firstStepConfirmed: false,
			errors: [],
			engaged: [],
			stepIndex: 0,
			thankYou: false,

			// Each array represents the required fields of a step
			required: [
				[
					'mainModeOfTransport', 'frequencyWalk', 'frequencyBike',
					'frequencyTransit', 'frequencyCar', 'frequencyCarCoride', 'frequencyMisc',
				],
				[
					'previousMainModeOfTransport', 'previousFrequencyWalk', 'previousFrequencyBike',
					'previousFrequencyTransit', 'previousFrequencyCar', 'previousFrequencyCarCoride', 'previousFrequencyMisc',
				],
				[
					'usedResvana'
				],
			],
		}
	},

	watch: {
		numberOfTrips(val) { this.engaged.push('numberOfTrips') },
		mainModeOfTransport(val) { this.engaged.push('mainModeOfTransport') },
		frequencyWalk(val) { this.engaged.push('frequencyWalk') },
		frequencyBike(val) { this.engaged.push('frequencyBike') },
		frequencyTransit(val) { this.engaged.push('frequencyTransit') },
		frequencyCar(val) { this.engaged.push('frequencyCar') },
		frequencyCarCoride(val) { this.engaged.push('frequencyCarCoride') },
		frequencyMisc(val) { this.engaged.push('frequencyMisc') },

		weeklyWalking(val) { this.engaged.push('weeklyWalking') },
		weeklyBike(val) { this.engaged.push('weeklyBike') },
		weeklyTransit(val) { this.engaged.push('weeklyTransit') },
		weeklyCar(val) { this.engaged.push('weeklyCar') },
		weeklyCarCoride(val) { this.engaged.push('weeklyCarCoride') },
		weeklyMisc(val) { this.engaged.push('weeklyMisc') },
		weeklyElectricity(val) { this.engaged.push('weeklyElectricity') },
		weeklyGas(val) { this.engaged.push('weeklyGas') },
		weeklyHybrid(val) { this.engaged.push('weeklyHybrid') },

		previousNumberOfTrips(val) { this.engaged.push('previousNumberOfTrips') },
		previousMainModeOfTransport(val) { this.engaged.push('previousMainModeOfTransport') },
		previousFrequencyWalk(val) { this.engaged.push('previousFrequencyWalk') },
		previousFrequencyBike(val) { this.engaged.push('previousFrequencyBike') },
		previousFrequencyTransit(val) { this.engaged.push('previousFrequencyTransit') },
		previousFrequencyCar(val) { this.engaged.push('previousFrequencyCar') },
		previousFrequencyCarCoride(val) { this.engaged.push('previousFrequencyCarCoride') },
		previousFrequencyMisc(val) { this.engaged.push('previousFrequencyMisc') },

		previousWeeklyWalking(val) { this.engaged.push('previousWeeklyWalking') },
		previousWeeklyBike(val) { this.engaged.push('previousWeeklyBike') },
		previousWeeklyTransit(val) { this.engaged.push('previousWeeklyTransit') },
		previousWeeklyCar(val) { this.engaged.push('previousWeeklyCar') },
		previousWeeklyCarCoride(val) { this.engaged.push('previousWeeklyCarCoride') },
		previousWeeklyMisc(val) { this.engaged.push('previousWeeklyMisc') },
		previousWeeklyElectricity(val) { this.engaged.push('previousWeeklyElectricity') },
		previousWeeklyGas(val) { this.engaged.push('previousWeeklyGas') },
		previousWeeklyHybrid(val) { this.engaged.push('previousWeeklyHybrid') },

		usedResvana(val) { this.engaged.push('usedResvana') },
		resvanaEffect(val) { this.engaged.push('resvanaEffect') },
		employerNudging(val) { this.engaged.push('employerNudging') },
		resvanaRecommend(val) { this.engaged.push('resvanaRecommend') },
		'more.walking': function (val) { this.engaged.push('moreWalking') },
		'more.biking': function (val) { this.engaged.push('moreBiking') },
		'more.transit': function (val) { this.engaged.push('moreTransit') },
		'more.car': function (val) { this.engaged.push('moreCar') },
	},

	methods: {
		selectFrequency(property, value) {
			this[property] = value
		},

		selectModeOfTransport(mode) {
			this.mainModeOfTransport = mode
		},

		selectpreviousModeOfTransport(mode) {
			this.previousMainModeOfTransport = mode
		},

		confirmFirstStep() {
			if ( ! this.mainModeOfTransport) {
				alert('Var god och välj ditt huvudsakliga färdsätt')
				return
			}

			this.firstStepConfirmed = true
		},

		nextStep() {
			if ( ! this.validate()) {
				alert('Var god och fyll i alla rödmarkerade fält')
				return
			}

			window.scrollTo(0, 0)
			this.stepIndex++
			this.saveState()
		},

		validate() {
			this.errors = []

			let required = [...this.required[this.stepIndex]]

			if (this.stepIndex === 0) {
				if (parseInt(this.frequencyWalk) > 0) { required.push('weeklyWalking') }
				if (parseInt(this.frequencyBike) > 0) { required.push('weeklyBike') }
				if (parseInt(this.frequencyTransit) > 0) { required.push('weeklyTransit') }
				if (parseInt(this.frequencyCar) > 0) { required.push('weeklyCar') }
				if (parseInt(this.frequencyCarCoride) > 0) { required.push('weeklyCarCoride') }
				if (parseInt(this.frequencyMisc) > 0) { required.push('weeklyMisc') }

				if (this.frequencyMisc || this.frequencyBike || this.frequencyTransit || this.frequencyCar || this.frequencyCarCoride || this.frequencyMisc) {
					required.push('numberOfTrips')
				}
			}

			if (this.stepIndex === 1) {
				if (parseInt(this.previousFrequencyWalk) > 0) { required.push('previousWeeklyWalking') }
				if (parseInt(this.previousFrequencyBike) > 0) { required.push('previousWeeklyBike') }
				if (parseInt(this.previousFrequencyTransit) > 0) { required.push('previousWeeklyTransit') }
				if (parseInt(this.previousFrequencyCar) > 0) { required.push('previousWeeklyCar') }
				if (parseInt(this.previousFrequencyCarCoride) > 0) { required.push('previousWeeklyCarCoride') }
				if (parseInt(this.previousFrequencyMisc) > 0) { required.push('previousWeeklyMisc') }

				if (this.previousFrequencyWalk || this.previousFrequencyBike || this.previousFrequencyTransit || this.previousFrequencyCar || this.previousFrequencyCarCoride || this.previousFrequencyMisc) {
					required.push('previousNumberOfTrips')
				}
			}

			if (this.stepIndex === 2) {

				if (this.usedResvana === 'Ja') {
					required = ['moreWalking', 'moreBiking', 'moreTransit', 'moreCar']
				}
			}

			this.errors = required.filter(key => {
				return ! this.engaged.includes(key) || (this.$data[key] instanceof Array && this.$data[key].length === 0)
			})

			if (this.stepIndex === 0) {
				// If weeklyCar > 0, then the  type of fuels should add up
				if (this.weeklyCar > 0 && (parseInt(this.weeklyElectricity || 0) + parseInt(this.weeklyGas || 0) + parseInt(this.weeklyHybrid || 0) != this.weeklyCar)) {
					alert('Summan av drivmedel måste bli ' + this.weeklyCar)
					this.errors.push('weeklyElectricity')
					this.errors.push('weeklyGas')
					this.errors.push('weeklyHybrid')
				}
			}

			if (this.stepIndex === 1) {
				// If previousweeklyCar > 0, then the type of fuels should add up
				if (this.previousWeeklyCar > 0 && (parseInt(this.previousWeeklyElectricity || 0) + parseInt(this.previousWeeklyGas || 0) + parseInt(this.previousWeeklyHybrid || 0) != this.previousWeeklyCar)) {
					alert('Summan av drivmedel måste bli ' + this.previousWeeklyCar)
					this.errors.push('previousWeeklyElectricity')
					this.errors.push('previousWeeklyGas')
					this.errors.push('previousWeeklyHybrid')
				}
			}

			// Remove most items if the person didn't use the service
			if (this.usedResvana === 'Nej') {
				required = []
			}

			this.errors =  this.errors.concat(required.filter(val => ! this.engaged.includes(val)))

			return this.errors.length === 0
		},

		prevStep() {
			window.scrollTo(0, 0)
			this.stepIndex--
		},

		saveState() {
			axios.post(this.$apiUrl + '/api/survey/1', {
				...this.$data,
				uuid: this.uuid,
				slug: this.slug,
				seconds_passed: performance ? parseInt(performance.now() / 1000) : null,
				completed_pages: this.stepIndex + 1
			})
		},

		submitResponse() {
			if ( ! this.validate()) {
				alert('Var god och fyll i alla rödmarkerade fält')
				return
			}

			axios.post(this.$apiUrl + '/api/survey/1', {
				...this.$data,
				uuid: this.uuid,
				slug: this.slug,
				seconds_passed: performance ? parseInt(performance.now() / 1000) : null,
				completed_pages: this.stepIndex + 1,
				complete: true
			}).then(res => {
				this.thankYou = true
				this.$store.state.surveySent = true
				//this.$router.push({name: 'SurveyThanks', params: {slug: this.slug}})
            })
		}
	},

	computed: {
		serviceImage() {
			return this.$store.state.client.customization?.service_img
		},

		slug() {
			// Cookie slug precedence
			return this.$route.params.slug || this.$getCookie('openEndUser')
		},

		endUser() {
			return this.$store.state.endUser
		},

		weeklySum() {
			return parseInt(this.weeklyWalking || 0) + parseInt(this.weeklyBike || 0) + parseInt(this.weeklyTransit || 0) + parseInt(this.weeklyCar || 0) + parseInt(this.weeklyCarCoride || 0) + parseInt(this.weeklyMisc || 0)
		},

		computedWeeklyAverage() {
			if ( ! this.numberOfTrips) {
				return 0
			}

			return (this.weeklySum / this.numberOfTrips).toFixed(0).replace('.', ',')
		},

		yearlyDistance() {
			return this.weeklySum * 52
		},

		previousWeeklySum() {
			return parseInt(this.previousWeeklyWalking || 0) + parseInt(this.previousWeeklyBike || 0) + parseInt(this.previousWeeklyTransit || 0) + parseInt(this.previousWeeklyCar || 0) + parseInt(this.previousWeeklyCarCoride || 0) + parseInt(this.previousWeeklyMisc || 0)
		},

		previousComputedWeeklyAverage() {
			if ( ! this.previousNumberOfTrips) {
				return 0
			}

			return (this.previousWeeklySum / this.previousNumberOfTrips).toFixed(0).replace('.', ',')
		},

		previousYearlyDistance() {
			return this.previousWeeklySum * 52
		},
	}
}
</script>